import { useState, useEffect, useRef } from 'react'
import { Camera, Edit } from 'lucide-react'
import Sidebar from '../../components/layout/Sidebar'
import Button from '../../components/common/Button'
import { storageService } from '../../services'
import { User } from '../../types/user.types'
import { API_BASE_URL, API_ENDPOINTS } from '../../constants'
import { toast } from 'react-toastify'

interface FarmerProfile {
  firstName: string
  lastName: string
  phone: string
  email?: string
  address?: string
  subdistrict?: string
  district?: string
  province?: string
  postalCode?: string
  nationalId?: string
  profileImage?: string
}

function ProfilePage() {
  const [profile, setProfile] = useState<FarmerProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      const token = storageService.getAuthToken()
      const user = storageService.getUser<User>()

      // Fetch farmer profile
      const profileResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.FARMERS.PROFILE}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (profileResponse.ok) {
        const profileData = await profileResponse.json()
        setProfile({
          ...profileData,
          email: user?.email || profileData.email
        })
      }
    } catch (error) {
      console.error('Error fetching profile:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('กรุณาเลือกไฟล์รูปภาพเท่านั้น')
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('ขนาดไฟล์ต้องไม่เกิน 5MB')
        return
      }

      setSelectedFile(file)

      // Create preview URL
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string)
      }
      reader.readAsDataURL(file)

      toast.success('เลือกรูปภาพสำเร็จ')
    }
  }

  const handleImageClick = () => {
    fileInputRef.current?.click()
  }

  const handleFieldChange = (field: keyof FarmerProfile, value: string) => {
    setProfile(prev => prev ? { ...prev, [field]: value } : null)
  }

  const handleSaveProfile = async () => {
    try {
      const token = storageService.getAuthToken()
      const formData = new FormData()

      // Add profile fields
      if (profile?.firstName) formData.append('firstName', profile.firstName)
      if (profile?.lastName) formData.append('lastName', profile.lastName)
      if (profile?.phone) formData.append('phone', profile.phone)
      if (profile?.address) formData.append('address', profile.address)
      if (profile?.subdistrict) formData.append('subdistrict', profile.subdistrict)
      if (profile?.district) formData.append('district', profile.district)
      if (profile?.province) formData.append('province', profile.province)
      if (profile?.postalCode) formData.append('postalCode', profile.postalCode)

      // Add profile image if selected
      if (selectedFile) {
        formData.append('profileImage', selectedFile)
      }

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.FARMERS.PROFILE}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      })

      if (response.ok) {
        toast.success('บันทึกข้อมูลสำเร็จ')
        setSelectedFile(null)
        setPreviewUrl(null)
        setIsEditing(false)
        await fetchProfile() // Refresh profile data
      } else {
        const error = await response.json()
        toast.error(error.message || 'บันทึกข้อมูลไม่สำเร็จ')
      }
    } catch (error) {
      console.error('Error saving profile:', error)
      toast.error('เกิดข้อผิดพลาดในการบันทึกข้อมูล')
    }
  }

  return (
    <div className="flex min-h-screen bg-white">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <div className="flex-1 pt-[64px]">
        {/* Header */}
        <div className="bg-white border-b border-border-light px-4 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8">
            {/* เกษตรกร Section */}
            <div>
              <h2 className="text-base lg:text-lg font-semibold text-text-primary">เกษตรกร</h2>
              <p className="text-xs lg:text-sm text-text-secondary">
                {isLoading ? 'กำลังโหลด...' : profile ? `สวัสดี, คุณ${profile.firstName} ${profile.lastName}` : 'สวัสดี'}
              </p>
            </div>

            {/* Divider */}
            <div className="hidden sm:block h-12 w-px bg-border-light" />

            {/* หน้าแรก Section */}
            <div>
              <h2 className="text-base lg:text-lg font-semibold text-text-primary">หน้าแรก</h2>
              <p className="text-xs lg:text-sm text-primary-800">
                คุณกำลังดูอยู่ที่ <span className="font-semibold">ข้อมูลส่วนตัว</span>
              </p>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-4 lg:p-8 bg-background-secondary min-h-screen">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <h1 className="text-xl lg:text-2xl font-semibold text-text-primary">ข้อมูลส่วนตัว</h1>

            {/* Edit Button */}
            <Button
              variant="primary"
              size="md"
              className="flex items-center gap-2 w-full sm:w-auto"
              onClick={() => setIsEditing(!isEditing)}
            >
              <Edit className="w-4 h-4" />
              {isEditing ? 'ยกเลิก' : 'แก้ไขข้อมูล'}
            </Button>
          </div>

          {/* Profile Form */}
          {isLoading ? (
            <div className="bg-white rounded-lg p-4 lg:p-8 text-center">
              <p className="text-text-secondary">กำลังโหลดข้อมูล...</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg p-4 lg:p-8">
              <div className="max-w-4xl mx-auto">
                {/* Profile Image */}
                <div className="flex justify-center mb-8">
                  <div className="relative">
                    <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full overflow-hidden bg-background-tertiary border-4 border-white shadow-lg">
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt="Profile Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : profile?.profileImage ? (
                        <img
                          src={profile.profileImage}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-primary-100">
                          <span className="text-4xl text-primary-800 font-semibold">
                            {profile?.firstName?.charAt(0) || 'U'}
                          </span>
                        </div>
                      )}
                    </div>
                    {isEditing && (
                      <button
                        type="button"
                        onClick={handleImageClick}
                        className="absolute bottom-0 right-0 bg-primary-800 text-white p-3 rounded-full hover:bg-primary-900 shadow-lg"
                      >
                        <Camera className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Form Fields */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                  {/* ชื่อ */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      ชื่อ <span className="text-error">*</span>
                    </label>
                    <input
                      type="text"
                      value={profile?.firstName || ''}
                      onChange={(e) => handleFieldChange('firstName', e.target.value)}
                      disabled={!isEditing}
                      className="w-full h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800 disabled:bg-background-tertiary disabled:text-text-secondary"
                    />
                  </div>

                  {/* นามสกุล */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      นามสกุล <span className="text-error">*</span>
                    </label>
                    <input
                      type="text"
                      value={profile?.lastName || ''}
                      onChange={(e) => handleFieldChange('lastName', e.target.value)}
                      disabled={!isEditing}
                      className="w-full h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800 disabled:bg-background-tertiary disabled:text-text-secondary"
                    />
                  </div>

                  {/* อีเมล */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      อีเมล <span className="text-error">*</span>
                    </label>
                    <input
                      type="email"
                      value={profile?.email || ''}
                      onChange={(e) => handleFieldChange('email', e.target.value)}
                      disabled={!isEditing}
                      className="w-full h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800 disabled:bg-background-tertiary disabled:text-text-secondary"
                    />
                    {profile?.email && (
                      <p className="text-xs text-text-tertiary mt-1">
                        (อนุญาตให้ใช้ต่อเมื่อยืนยัน"email" หรือ "ทาง" เท่านั้น)
                      </p>
                    )}
                  </div>

                  {/* เบอร์โทรศัพท์ */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      เบอร์โทรศัพท์ <span className="text-error">*</span>
                    </label>
                    <input
                      type="text"
                      value={profile?.phone || ''}
                      onChange={(e) => handleFieldChange('phone', e.target.value)}
                      placeholder="098-1234567"
                      disabled={!isEditing}
                      className="w-full h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800 disabled:bg-background-tertiary disabled:text-text-secondary"
                    />
                  </div>

                  {/* รูปภาพ */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      รูปภาพ
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={selectedFile ? selectedFile.name : ''}
                        placeholder="ไม่มีไฟล์ที่เลือก"
                        disabled
                        className="flex-1 h-input px-4 border border-border-medium rounded-lg text-text-secondary bg-background-tertiary"
                      />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      {isEditing && (
                        <Button
                          type="button"
                          variant="secondary"
                          size="md"
                          onClick={handleImageClick}
                        >
                          เลือกไฟล์
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Address Section */}
                <div className="mt-8 pt-6 border-t border-border-light">
                  <h3 className="text-lg font-semibold text-text-primary mb-4">ที่อยู่</h3>

                  <div className="grid grid-cols-1 gap-6">
                    {/* ที่อยู่ */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        ที่อยู่
                      </label>
                      <input
                        type="text"
                        value={profile?.address || ''}
                        onChange={(e) => handleFieldChange('address', e.target.value)}
                        disabled={!isEditing}
                        className="w-full h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800 disabled:bg-background-tertiary disabled:text-text-secondary"
                      />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                      {/* ตำบล */}
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          ตำบล/แขวง
                        </label>
                        <input
                          type="text"
                          value={profile?.subdistrict || ''}
                          onChange={(e) => handleFieldChange('subdistrict', e.target.value)}
                          disabled={!isEditing}
                          className="w-full h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800 disabled:bg-background-tertiary disabled:text-text-secondary"
                        />
                      </div>

                      {/* อำเภอ */}
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          อำเภอ/เขต
                        </label>
                        <input
                          type="text"
                          value={profile?.district || ''}
                          onChange={(e) => handleFieldChange('district', e.target.value)}
                          disabled={!isEditing}
                          className="w-full h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800 disabled:bg-background-tertiary disabled:text-text-secondary"
                        />
                      </div>

                      {/* จังหวัด */}
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          จังหวัด
                        </label>
                        <input
                          type="text"
                          value={profile?.province || ''}
                          onChange={(e) => handleFieldChange('province', e.target.value)}
                          disabled={!isEditing}
                          className="w-full h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800 disabled:bg-background-tertiary disabled:text-text-secondary"
                        />
                      </div>

                      {/* รหัสไปรษณีย์ */}
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          รหัสไปรษณีย์
                        </label>
                        <input
                          type="text"
                          value={profile?.postalCode || ''}
                          onChange={(e) => handleFieldChange('postalCode', e.target.value)}
                          disabled={!isEditing}
                          className="w-full h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800 disabled:bg-background-tertiary disabled:text-text-secondary"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                {isEditing && (
                  <div className="mt-8 flex justify-end gap-4">
                    <Button
                      variant="secondary"
                      size="lg"
                      onClick={() => setIsEditing(false)}
                    >
                      ยกเลิก
                    </Button>
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleSaveProfile}
                    >
                      บันทึกข้อมูล
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProfilePage
